import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import { useThemeMode } from '../../contexts/ThemeModeContext';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Dialog,
  DialogContent,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  Autocomplete,
  InputAdornment,
  ButtonBase,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  DeleteOutline as DeleteIcon,
  AttachFile as AttachFileIcon,
  CalendarToday as CalendarIcon,
  AccessTime as ClockIcon,
  InfoOutlined as InfoIcon
} from '@mui/icons-material';
import { patientService, orderService, procedureService, departmentService, referrerService, roomService } from '../../services/ris';
import { getRadiologists } from '../../services/users';
import { parseDicomDateParts } from '../../services/utils';

// Font style helpers
const satoshiBold = {
  fontFamily: '"Satoshi", sans-serif',
  fontWeight: 700,
};
const satoshiMedium = {
  fontFamily: '"Satoshi", sans-serif',
  fontWeight: 500,
};
const satoshiRegular = {
  fontFamily: '"Satoshi", sans-serif',
  fontWeight: 400,
};

const CLINICAL_WORKFLOW = {
  XRAY: {
    label: "X-Ray",
    bodyParts: {
      Chest: {
        label: "Chest",
        exams: ["Chest X-Ray", "Ribs X-Ray", "Sternum X-Ray", "Clavicle X-Ray", "Scapula X-Ray"]
      },
      Abdomen: {
        label: "Abdomen",
        exams: ["Abdomen X-Ray", "KUB X-Ray"]
      },
      Pelvis: {
        label: "Pelvis",
        exams: ["Pelvis X-Ray", "Hip X-Ray"]
      },
      Head: {
        label: "Head",
        exams: ["Skull X-Ray", "Facial Bones X-Ray", "Sinuses X-Ray", "Mandible X-Ray", "Orbit X-Ray", "Nasal Bones X-Ray"]
      },
      Spine: {
        label: "Spine",
        exams: ["Cervical Spine X-Ray", "Thoracic Spine X-Ray", "Lumbar Spine X-Ray", "Sacrum X-Ray", "Coccyx X-Ray"]
      },
      "Upper Limb": {
        label: "Upper Limb",
        exams: ["Shoulder X-Ray", "Clavicle X-Ray", "Scapula X-Ray", "Humerus X-Ray", "Elbow X-Ray", "Forearm X-Ray", "Wrist X-Ray", "Hand X-Ray"]
      },
      "Lower Limb": {
        label: "Lower Limb",
        exams: ["Femur X-Ray", "Knee X-Ray", "Tibia/Fibula X-Ray", "Ankle X-Ray", "Foot X-Ray"]
      },
      Breast: {
        label: "Breast",
        exams: ["Bilateral Mammogram X-Ray", "Left Mammogram X-Ray", "Right Mammogram X-Ray"]
      }
    }
  },
  CT: {
    label: "CT",
    bodyParts: {
      Chest: {
        label: "Chest",
        exams: ["Chest CT", "Lungs CT", "High-Res Chest CT", "CT Pulmonary Angiography (CTPA)"]
      },
      Abdomen: {
        label: "Abdomen",
        exams: ["Abdomen CT", "Liver CT", "Pancreas CT", "Kidneys CT"]
      },
      Pelvis: {
        label: "Pelvis",
        exams: ["Pelvis CT", "Urogram CT"]
      },
      Head: {
        label: "Head",
        exams: ["Brain CT", "Facial Bones CT", "Orbit CT", "Sinuses CT", "Temporal Bone CT"]
      },
      Spine: {
        label: "Spine",
        exams: ["Cervical Spine CT", "Thoracic Spine CT", "Lumbar Spine CT"]
      },
      "Upper Limb": {
        label: "Upper Limb",
        exams: ["Shoulder CT", "Elbow CT", "Wrist CT", "Hand CT"]
      },
      "Lower Limb": {
        label: "Lower Limb",
        exams: ["Hip CT", "Knee CT", "Ankle CT", "Foot CT"]
      }
    }
  },
  MRI: {
    label: "MRI",
    bodyParts: {
      Brain: {
        label: "Brain",
        exams: ["Brain MRI", "Pituitary MRI", "Orbits MRI", "IAC MRI"]
      },
      Spine: {
        label: "Spine",
        exams: ["Cervical Spine MRI", "Thoracic Spine MRI", "Lumbar Spine MRI"]
      },
      "Upper Limb": {
        label: "Upper Limb",
        exams: ["Shoulder MRI", "Elbow MRI", "Wrist MRI", "Hand MRI"]
      },
      "Lower Limb": {
        label: "Lower Limb",
        exams: ["Hip MRI", "Knee MRI", "Ankle MRI", "Foot MRI"]
      },
      Abdomen: {
        label: "Abdomen",
        exams: ["Abdomen MRI", "MRCP", "Liver MRI", "Kidneys MRI"]
      },
      Pelvis: {
        label: "Pelvis",
        exams: ["Pelvis MRI", "Prostate MRI", "Uterus/Ovaries MRI"]
      }
    }
  },
  US: {
    label: "US",
    bodyParts: {
      Abdomen: {
        label: "Abdomen",
        exams: ["Complete Abdomen US", "Gallbladder US", "Renal US", "Spleen US"]
      },
      Pelvis: {
        label: "Pelvis",
        exams: ["Pelvis US", "Transvaginal US", "Prostate US"]
      },
      Breast: {
        label: "Breast",
        exams: ["Breast US (Left)", "Breast US (Right)", "Bilateral Breast US"]
      },
      Head: {
        label: "Head",
        exams: ["Thyroid US", "Neck US"]
      },
      "Lower Limb": {
        label: "Lower Limb",
        exams: ["Venous Doppler US", "Arterial Doppler US"]
      }
    }
  },
  MAMMO: {
    label: "Mammo",
    bodyParts: {
      Breast: {
        label: "Breast",
        exams: ["Bilateral Screening Mammography", "Bilateral Diagnostic Mammography", "Unilateral Mammography"]
      }
    }
  },
  FLUORO: {
    label: "Fluoroscopy",
    bodyParts: {
      Abdomen: {
        label: "Abdomen",
        exams: ["Barium Swallow", "Barium Meal", "Barium Enema"]
      },
      Pelvis: {
        label: "Pelvis",
        exams: ["Hysterosalpingography (HSG)", "Micturating Cystourethrogram (MCU)"]
      }
    }
  }
};

const MODALITY_OPTIONS = [
  { value: 'XRAY', label: 'X-Ray' },
  { value: 'CT', label: 'CT' },
  { value: 'MRI', label: 'MRI' },
  { value: 'US', label: 'US' },
  { value: 'MAMMO', label: 'Mammo' },
  { value: 'FLUORO', label: 'Fluoroscopy' },
  { value: 'OTHER', label: 'Other' }
];

const DEFAULT_BODY_PARTS = [
  'Chest', 'Abdomen', 'Pelvis', 'Head', 'Spine', 'Upper Limb', 'Lower Limb', 'Breast', 'Other'
];

const VIEW_OPTIONS = [
  { value: 'PA', label: 'PA' },
  { value: 'AP', label: 'AP' },
  { value: 'LAT', label: 'Lateral' },
  { value: 'OBL', label: 'Oblique' },
  { value: 'PA_LAT', label: 'PA + Lateral' },
  { value: 'OTHER', label: 'Other' }
];

const PRIORITY_OPTIONS = [
  { value: 'ROUTINE', label: 'Routine' },
  { value: 'STAT', label: 'STAT' }
];

export default function CreateOrder({ open, onClose, onSuccess, preselectedPatient, editOrder, onClearPatient, initialScheduling }) {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const cardBg = isDark ? '#1E1E1E' : '#FFFFFF';
  const inputBg = isDark ? '#121212' : '#FFFFFF';
  const inputBgAlt = isDark ? '#121212' : '#F8FAFC';
  const cardBorderColor = isDark ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0';
  const cardBorder = `1.5px solid ${cardBorderColor}`;
  const hoverBorderColor = isDark ? 'rgba(255, 255, 255, 0.2)' : '#CBD5E1';
  const textColorPrimary = isDark ? '#F1F5F9' : '#0F172A';
  const textColorSecondary = isDark ? '#CBD5E1' : '#475569';
  const textColorMuted = isDark ? '#94A3B8' : '#64748B';
  const patientCardBg = isDark ? '#121212' : '#FFFFFF';
  const hoverBgSubtle = isDark ? 'rgba(255,255,255,0.05)' : '#F8FAFC';

  const [patient, setPatient] = useState(preselectedPatient || null);
  const [patientSearch, setPatientSearch] = useState('');
  const [patientResults, setPatientResults] = useState([]);
  const [searchingPatient, setSearchingPatient] = useState(false);

  const [departments, setDepartments] = useState([]);
  const [referrers, setReferrers] = useState([]);
  const [radiologists, setRadiologists] = useState([]);
  const [selectedRadiologist, setSelectedRadiologist] = useState(null);

  // Selections
  const [selectedModality, setSelectedModality] = useState(initialScheduling?.modality || '');
  const [selectedBodyPart, setSelectedBodyPart] = useState('');
  const [selectedExamType, setSelectedExamType] = useState('');
  const [selectedView, setSelectedView] = useState('');
  const [selectedPriority, setSelectedPriority] = useState('');

  // Custom text fallback states
  const [customModality, setCustomModality] = useState('');
  const [customBodyPart, setCustomBodyPart] = useState('');
  const [customExamType, setCustomExamType] = useState('');
  const [customView, setCustomView] = useState('');

  // Other form fields
  const [clinicalIndication, setClinicalIndication] = useState('');
  const [referrer, setReferrer] = useState(null);
  const [referrerName, setReferrerName] = useState('');
  const [department, setDepartment] = useState(null);
  const [departmentName, setDepartmentName] = useState('');
  const [scheduledDate, setScheduledDate] = useState(new Date().toISOString().slice(0, 10));
  const [scheduledTime, setScheduledTime] = useState('');
  const [attachedFile, setAttachedFile] = useState(null);

  // Room/equipment booking
  const [availableRooms, setAvailableRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const pendingRoomId = useRef(null);

  // Seed scheduling fields when opened from the Scheduling Board (create mode only —
  // editOrder, handled separately below, takes priority if both are somehow present)
  useEffect(() => {
    if (!initialScheduling || editOrder) return;
    setScheduledDate(initialScheduling.scheduledDate || new Date().toISOString().slice(0, 10));
    setScheduledTime(initialScheduling.scheduledTime || '');
    if (initialScheduling.modality) setSelectedModality(initialScheduling.modality);
    pendingRoomId.current = initialScheduling.roomId || null;
  }, [initialScheduling, editOrder]);

  // Success Modal Dialog states
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdOrderDetails, setCreatedOrderDetails] = useState(null);

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Load reference data
  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        const [depRes, refRes, radRes] = await Promise.all([
          departmentService.list(),
          referrerService.list(),
          getRadiologists()
        ]);
        setDepartments(depRes.data.departments || []);
        setReferrers(refRes.data.referrers || []);
        setRadiologists(radRes.radiologists || []);
      } catch (err) {
        console.error('Failed to load reference data:', err);
      }
    };
    loadReferenceData();
  }, []);

  // Update selected patient when preselectedPatient changes
  useEffect(() => {
    if (preselectedPatient) {
      setPatient(preselectedPatient);
    }
  }, [preselectedPatient]);

  // Update form fields if editOrder is provided
  useEffect(() => {
    if (editOrder) {
      if (editOrder.patient) {
        setPatient(editOrder.patient);
      }
      setSelectedModality(editOrder.modality || '');
      setSelectedBodyPart(editOrder.bodyPart || '');
      
      // Parse Exam Type and View from procedureDescription if needed
      let exam = editOrder.procedureDescription || '';
      let viewVal = '';
      if (exam.includes(' (')) {
        const parts = exam.split(' (');
        exam = parts[0];
        viewVal = parts[1].replace(')', '');
      }
      setSelectedExamType(exam);
      setSelectedView(viewVal);

      setSelectedPriority(editOrder.priority || '');
      setClinicalIndication(editOrder.clinicalIndication || '');
      setScheduledDate(editOrder.scheduledDate ? new Date(editOrder.scheduledDate).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10));
      setScheduledTime(editOrder.scheduledTime || '');
      if (editOrder.assignedRoom) {
        pendingRoomId.current = editOrder.assignedRoom._id || editOrder.assignedRoom;
      }
      
      if (editOrder.department) {
        setDepartment(editOrder.department);
      }
      setDepartmentName(editOrder.departmentName || '');
      if (editOrder.referrer) {
        setReferrer(editOrder.referrer);
      }
      setReferrerName(editOrder.referrerName || '');
      
      if (editOrder.assignedRadiologist) {
        setSelectedRadiologist({
          _id: editOrder.assignedRadiologist,
          firstName: editOrder.assignedRadiologistName?.split(' ')[0] || '',
          lastName: editOrder.assignedRadiologistName?.split(' ').slice(1).join(' ') || ''
        });
      }
    }
  }, [editOrder]);

  // Search patients with debounce
  const searchPatients = useCallback(async (term) => {
    if (!term || term.length < 2) {
      setPatientResults([]);
      return;
    }
    setSearchingPatient(true);
    try {
      const { data } = await patientService.search({ q: term, limit: 10, systemSource: 'RIS' });
      setPatientResults(data.patients || []);
    } catch (err) {
      console.error('Patient search error:', err);
    } finally {
      setSearchingPatient(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchPatients(patientSearch), 300);
    return () => clearTimeout(timer);
  }, [patientSearch, searchPatients]);

  // Load rooms for the selected modality (room booking is optional)
  const modalityForRooms = selectedModality === 'OTHER' ? customModality : selectedModality;
  useEffect(() => {
    if (!modalityForRooms) {
      setAvailableRooms([]);
      setSelectedRoom(null);
      return;
    }
    roomService.list({ modality: modalityForRooms }).then(({ data }) => {
      const rooms = data.rooms || [];
      setAvailableRooms(rooms);
      if (pendingRoomId.current) {
        const match = rooms.find(r => r._id === pendingRoomId.current);
        if (match) {
          setSelectedRoom(match);
          pendingRoomId.current = null;
        }
      }
    }).catch(() => setAvailableRooms([]));
  }, [modalityForRooms]);

  const handleModalityChange = (val) => {
    setSelectedModality(val);
    setSelectedBodyPart('');
    setSelectedExamType('');
    setSelectedView('');
    setCustomModality('');
    setCustomBodyPart('');
    setCustomExamType('');
    setCustomView('');
    setSelectedRoom(null);
  };

  const handleBodyPartChange = (val) => {
    setSelectedBodyPart(val);
    setSelectedExamType('');
    setCustomBodyPart('');
    setCustomExamType('');
  };

  const handleExamTypeChange = (val) => {
    setSelectedExamType(val);
    setCustomExamType('');
  };

  const handleViewChange = (val) => {
    setSelectedView(val);
    setCustomView('');
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setAttachedFile(e.target.files[0]);
    }
  };

  const handleClearPatient = () => {
    setPatient(null);
    setPatientSearch('');
    setPatientResults([]);
    if (onClearPatient) {
      onClearPatient();
    }
  };

  const handleResetForm = () => {
    setPatient(null);
    setPatientSearch('');
    setPatientResults([]);
    setSelectedModality('');
    setSelectedBodyPart('');
    setSelectedExamType('');
    setSelectedView('');
    setSelectedPriority('');
    setCustomModality('');
    setCustomBodyPart('');
    setCustomExamType('');
    setCustomView('');
    setClinicalIndication('');
    setReferrer(null);
    setReferrerName('');
    setDepartment(null);
    setDepartmentName('');
    setScheduledDate(new Date().toISOString().slice(0, 10));
    setScheduledTime('');
    setAttachedFile(null);
    setShowSuccessModal(false);
    setCreatedOrderDetails(null);
  };

  // Reset order fields only — keeps the current patient selected so the user
  // can immediately create another order for the same patient.
  const handleResetFormKeepPatient = () => {
    setSelectedModality('');
    setSelectedBodyPart('');
    setSelectedExamType('');
    setSelectedView('');
    setSelectedPriority('');
    setCustomModality('');
    setCustomBodyPart('');
    setCustomExamType('');
    setCustomView('');
    setClinicalIndication('');
    setReferrer(null);
    setReferrerName('');
    setDepartment(null);
    setDepartmentName('');
    setScheduledDate(new Date().toISOString().slice(0, 10));
    setScheduledTime('');
    setAttachedFile(null);
    setShowSuccessModal(false);
    setCreatedOrderDetails(null);
  };

  // Get active lists
  const getBodyPartOptions = () => {
    if (selectedModality && CLINICAL_WORKFLOW[selectedModality]) {
      const workflow = CLINICAL_WORKFLOW[selectedModality];
      return Object.keys(workflow.bodyParts).map(key => ({
        value: key,
        label: workflow.bodyParts[key].label
      })).concat([{ value: 'OTHER', label: 'Other' }]);
    }
    return DEFAULT_BODY_PARTS.map(part => ({ value: part, label: part }));
  };

  const getExamTypeOptions = () => {
    if (selectedModality && selectedBodyPart && CLINICAL_WORKFLOW[selectedModality]?.bodyParts?.[selectedBodyPart]) {
      const exams = CLINICAL_WORKFLOW[selectedModality].bodyParts[selectedBodyPart].exams;
      return exams.map(exam => ({ value: exam, label: exam })).concat([{ value: 'OTHER', label: 'Other' }]);
    }
    return [{ value: 'OTHER', label: 'Other' }];
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    if (!patient) {
      setSnackbar({ open: true, message: 'Please select a patient', severity: 'error' });
      return;
    }
    
    const finalModality = selectedModality === 'OTHER' ? customModality : selectedModality;
    if (!finalModality) {
      setSnackbar({ open: true, message: 'Modality is required', severity: 'error' });
      return;
    }

    const finalBodyPart = selectedBodyPart === 'OTHER' ? customBodyPart : selectedBodyPart;
    if (!finalBodyPart) {
      setSnackbar({ open: true, message: 'Body part is required', severity: 'error' });
      return;
    }

    const finalExamType = selectedExamType === 'OTHER' ? customExamType : selectedExamType;
    if (!finalExamType) {
      setSnackbar({ open: true, message: 'Examination type is required', severity: 'error' });
      return;
    }

    if (!selectedPriority) {
      setSnackbar({ open: true, message: 'Priority is required', severity: 'error' });
      return;
    }

    const finalView = selectedView === 'OTHER' ? customView : selectedView;

    setLoading(true);
    try {
      if (selectedRoom && scheduledDate && scheduledTime) {
        try {
          const { data: availability } = await orderService.checkAvailability({
            roomId: selectedRoom._id,
            scheduledDate,
            scheduledTime,
            excludeOrderId: editOrder?._id || editOrder?.orderId || undefined
          });
          if (availability.conflict) {
            const proceed = window.confirm(
              `${selectedRoom.name} already has an order scheduled around this time (${availability.conflictingOrders.map(c => c.scheduledTime).join(', ')}). Continue anyway?`
            );
            if (!proceed) {
              setLoading(false);
              return;
            }
          }
        } catch (availErr) {
          console.error('Availability check failed:', availErr);
        }
      }

      const procedureCode = `${finalModality}-${finalBodyPart.toUpperCase().replace(/[^A-Z0-9]/g, '-')}`;
      const procedureDescription = finalView ? `${finalExamType} (${finalView})` : finalExamType;

      const payload = {
        patientId: patient._id,
        modality: finalModality,
        procedureCode: procedureCode,
        procedureDescription: procedureDescription,
        bodyPart: finalBodyPart,
        referrer: referrer || undefined,
        referrerName: referrerName || undefined,
        department: department || undefined,
        departmentName: departmentName || undefined,
        clinicalIndication: clinicalIndication || undefined,
        notes: attachedFile ? `Attached File: ${attachedFile.name}` : undefined,
        priority: selectedPriority,
        scheduledDate: scheduledDate,
        scheduledTime: scheduledTime || undefined,
        assignedRoom: selectedRoom?._id || undefined,
        assignedRadiologist: selectedRadiologist?._id || undefined,
        assignedRadiologistName: selectedRadiologist ? `${selectedRadiologist.firstName} ${selectedRadiologist.lastName}` : undefined
      };

      if (editOrder) {
        const id = editOrder._id || editOrder.orderId;
        const { data } = await orderService.update(id, payload);
        setSnackbar({ open: true, message: 'Order updated successfully', severity: 'success' });
        setTimeout(() => {
          onSuccess && onSuccess(data.order);
        }, 1000);
      } else {
        const { data } = await orderService.create(payload);
        
        // Capture success details and show modal
        setCreatedOrderDetails({
          patientName: `${patient.firstName} ${patient.lastName}`,
          patientMrn: patient.mrn || patient.patientId || '',
          examination: finalExamType,
          view: finalView ? (finalView.endsWith('View') ? finalView : `${finalView} View`) : 'Not Specified',
          priority: selectedPriority,
          accessionNumber: data.order.accessionNumber
        });
        setShowSuccessModal(true);
      }
    } catch (err) {
      const msg = err.response?.data?.error || (editOrder ? 'Failed to update order' : 'Failed to create order');
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const renderPillSelect = (label, options, value, onChange, isRequired = false) => {
    return (
      <Box sx={{ mb: 0.85, textAlign: 'left' }}>
        <Typography variant="body2" sx={{ ...satoshiBold, color: textColorPrimary, mb: 0.35, fontSize: '0.72rem' }}>
          {label} {isRequired && <span style={{ color: '#EF4444' }}>*</span>}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {options.map((opt) => {
            const isSelected = value === opt.value;
            return (
              <ButtonBase
                key={opt.value}
                onClick={() => onChange(opt.value)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  py: 0.25,
                  px: 0.85,
                  borderRadius: '12px',
                  border: '1.2px solid',
                  borderColor: isSelected ? '#0D9488' : cardBorderColor,
                  bgcolor: isSelected ? (isDark ? 'rgba(13, 148, 136, 0.15)' : '#F0FDFA') : cardBg,
                  color: isSelected ? (isDark ? '#2DD4BF' : '#0F172A') : textColorSecondary,
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    borderColor: isSelected ? '#0D9488' : hoverBorderColor,
                    bgcolor: isSelected ? (isDark ? 'rgba(13, 148, 136, 0.15)' : '#F0FDFA') : hoverBgSubtle,
                  }
                }}
              >
                {/* Radio button dot */}
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    border: '1.2px solid',
                    borderColor: isSelected ? '#0D9488' : '#94A3B8',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: cardBg,
                    flexShrink: 0
                  }}
                >
                  {isSelected && (
                    <Box
                      sx={{
                        width: 5,
                        height: 5,
                        borderRadius: '50%',
                        bgcolor: '#0D9488',
                      }}
                    />
                  )}
                </Box>
                <Typography sx={{ ...satoshiBold, fontSize: '0.72rem', userSelect: 'none' }}>
                  {opt.label}
                </Typography>
              </ButtonBase>
            );
          })}
        </Box>
      </Box>
    );
  };

  const formContent = (
    <Box sx={{ width: '100%' }}>
      {/* 2-Column Clinical Layout */}
      <Grid container spacing={1.25}>
        {/* LEFT COLUMN: Patient & Examination Specs */}
        <Grid item xs={12} md={6}>
          {/* Patient Card */}
          <Paper
            elevation={0}
            sx={{
              p: 1.1,
              mb: 1.1,
              borderRadius: '8px',
              border: cardBorder,
              bgcolor: isDark ? '#181818' : '#FBFDFD',
              textAlign: 'left'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="body2" sx={{ ...satoshiBold, color: textColorPrimary, fontSize: '0.78rem' }}>
                Patient <span style={{ color: '#EF4444' }}>*</span>
              </Typography>
              {patient && (
                <Button
                  size="small"
                  onClick={handleClearPatient}
                  startIcon={<DeleteIcon sx={{ fontSize: 13 }} />}
                  sx={{
                    ...satoshiBold,
                    textTransform: 'none',
                    color: textColorMuted,
                    borderColor: cardBorderColor,
                    borderRadius: '5px',
                    px: 1,
                    py: 0,
                    height: 22,
                    fontSize: '0.70rem',
                    '&:hover': {
                      borderColor: hoverBorderColor,
                      bgcolor: hoverBgSubtle
                    }
                  }}
                >
                  Change Patient
                </Button>
              )}
            </Box>

            {!patient ? (
              <Autocomplete
                options={patientResults}
                getOptionLabel={(p) => `${p.mrn} — ${p.firstName} ${p.lastName}`}
                inputValue={patientSearch}
                onInputChange={(_, v) => setPatientSearch(v)}
                onChange={(_, p) => setPatient(p)}
                loading={searchingPatient}
                noOptionsText={patientSearch.length < 2 ? 'Type at least 2 characters to search' : 'No patients found'}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search patient by name or patient ID..."
                    fullWidth
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: '#94A3B8', fontSize: 16 }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <>
                          {searchingPatient && <CircularProgress size={14} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                      sx: {
                        borderRadius: '6px',
                        height: 28,
                        fontFamily: '"Satoshi", sans-serif',
                        bgcolor: inputBg,
                        border: cardBorder,
                        color: textColorPrimary,
                        fontSize: '0.76rem',
                        '& input': { color: textColorPrimary, py: '2px', fontSize: '0.76rem' },
                        '& fieldset': { border: 'none' },
                        '&:hover': { borderColor: '#CBD5E1' },
                        '&.Mui-focused': { borderColor: '#00665C' }
                      }
                    }}
                  />
                )}
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.2,
                  p: 0.6,
                  px: 1,
                  borderRadius: '6px',
                  bgcolor: patientCardBg,
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#E2E8F0'}`,
                }}
              >
                <Box
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    bgcolor: '#00665C',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    ...satoshiBold,
                    fontSize: '0.70rem',
                    flexShrink: 0
                  }}
                >
                  {patient.firstName?.[0]}{patient.lastName?.[0]}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 1 }}>
                  <Typography sx={{ ...satoshiBold, color: textColorPrimary, fontSize: '0.78rem' }}>
                    {patient.firstName} {patient.lastName}
                  </Typography>
                  <Typography sx={{ ...satoshiMedium, color: textColorSecondary, fontSize: '0.70rem' }}>
                    MRN: <span style={{ fontWeight: 700, color: textColorPrimary }}>{patient.mrn || patient.patientId || '-'}</span>
                  </Typography>
                  <Typography sx={{ ...satoshiMedium, color: textColorSecondary, fontSize: '0.70rem' }}>
                    {patient.sex === 'M' ? 'Male' : patient.sex === 'F' ? 'Female' : 'Other'} • {(() => {
                      const parts = parseDicomDateParts(patient.birthDate);
                      if (!parts) return '-';
                      return `${parts.year}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`;
                    })()}
                  </Typography>
                </Box>
              </Box>
            )}
          </Paper>

          {/* Examination Details Card */}
          <Paper
            elevation={0}
            sx={{
              p: 1.1,
              borderRadius: '8px',
              border: cardBorder,
              bgcolor: isDark ? '#181818' : '#FBFDFD',
              textAlign: 'left'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.6 }}>
              <Typography variant="subtitle2" sx={{ ...satoshiBold, color: textColorPrimary, fontSize: '0.78rem' }}>
                Examination Details
              </Typography>
              {/* Priority inline pill toggle */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" sx={{ ...satoshiBold, color: textColorSecondary, fontSize: '0.68rem' }}>
                  Priority:
                </Typography>
                {PRIORITY_OPTIONS.map((opt) => {
                  const isSelected = selectedPriority === opt.value;
                  const isStat = opt.value === 'STAT';
                  return (
                    <ButtonBase
                      key={opt.value}
                      onClick={() => setSelectedPriority(opt.value)}
                      sx={{
                        py: 0.2,
                        px: 0.8,
                        borderRadius: '10px',
                        border: '1.2px solid',
                        borderColor: isSelected
                          ? (isStat ? '#EF4444' : '#0D9488')
                          : cardBorderColor,
                        bgcolor: isSelected
                          ? (isStat ? (isDark ? 'rgba(239,68,68,0.2)' : '#FEF2F2') : (isDark ? 'rgba(13,148,136,0.2)' : '#F0FDFA'))
                          : 'transparent',
                        color: isSelected
                          ? (isStat ? '#EF4444' : (isDark ? '#2DD4BF' : '#00665C'))
                          : textColorSecondary,
                        fontSize: '0.68rem',
                        ...satoshiBold,
                        transition: 'all 0.15s ease',
                      }}
                    >
                      {opt.label}
                    </ButtonBase>
                  );
                })}
              </Box>
            </Box>

            {/* Modality Pills */}
            {renderPillSelect("Modality", MODALITY_OPTIONS, selectedModality, handleModalityChange, true)}
            {selectedModality === 'OTHER' && (
              <TextField
                placeholder="Specify custom modality..."
                size="small"
                required
                fullWidth
                value={customModality}
                onChange={(e) => setCustomModality(e.target.value)}
                sx={{ mb: 0.8, maxWidth: 300, '& .MuiInputBase-input': { color: textColorPrimary, fontSize: '0.75rem', py: 0.4 } }}
                InputProps={{ sx: { borderRadius: '6px', height: 28, fontFamily: '"Satoshi", sans-serif' } }}
              />
            )}

            {/* Body Part Pills */}
            {selectedModality && renderPillSelect("Body Part", getBodyPartOptions(), selectedBodyPart, handleBodyPartChange, true)}
            {selectedBodyPart === 'OTHER' && (
              <TextField
                placeholder="Specify custom body part..."
                size="small"
                required
                fullWidth
                value={customBodyPart}
                onChange={(e) => setCustomBodyPart(e.target.value)}
                sx={{ mb: 0.8, maxWidth: 300, '& .MuiInputBase-input': { color: textColorPrimary, fontSize: '0.75rem', py: 0.4 } }}
                InputProps={{ sx: { borderRadius: '6px', height: 28, fontFamily: '"Satoshi", sans-serif' } }}
              />
            )}

            {/* Examination Type Pills */}
            {selectedModality && selectedBodyPart && renderPillSelect("Examination Type", getExamTypeOptions(), selectedExamType, handleExamTypeChange, true)}
            {selectedExamType === 'OTHER' && (
              <TextField
                placeholder="Specify custom examination type..."
                size="small"
                required
                fullWidth
                value={customExamType}
                onChange={(e) => setCustomExamType(e.target.value)}
                sx={{ mb: 0.8, maxWidth: 300, '& .MuiInputBase-input': { color: textColorPrimary, fontSize: '0.75rem', py: 0.4 } }}
                InputProps={{ sx: { borderRadius: '6px', height: 28, fontFamily: '"Satoshi", sans-serif' } }}
              />
            )}

            {/* View Pills */}
            {selectedModality && selectedBodyPart && selectedExamType && renderPillSelect("View (Projection)", VIEW_OPTIONS, selectedView, handleViewChange, true)}
            {selectedView === 'OTHER' && (
              <TextField
                placeholder="Specify custom view..."
                size="small"
                required
                fullWidth
                value={customView}
                onChange={(e) => setCustomView(e.target.value)}
                sx={{ mb: 0.8, maxWidth: 300, '& .MuiInputBase-input': { color: textColorPrimary, fontSize: '0.75rem', py: 0.4 } }}
                InputProps={{ sx: { borderRadius: '6px', height: 28, fontFamily: '"Satoshi", sans-serif' } }}
              />
            )}
          </Paper>
        </Grid>

        {/* RIGHT COLUMN: Clinical Indication, Ordering, Radiologist & Scheduling */}
        <Grid item xs={12} md={6}>
          {/* Clinical Context & Attachment Card */}
          <Paper
            elevation={0}
            sx={{
              p: 1.1,
              mb: 1.1,
              borderRadius: '8px',
              border: cardBorder,
              bgcolor: isDark ? '#181818' : '#FBFDFD',
              textAlign: 'left'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.35 }}>
              <Typography variant="body2" sx={{ ...satoshiBold, color: textColorPrimary, fontSize: '0.78rem' }}>
                Clinical Indication / Reason <span style={{ color: '#EF4444' }}>*</span>
              </Typography>
              <Typography variant="caption" sx={{ ...satoshiMedium, color: '#94A3B8', fontSize: '0.66rem' }}>
                {500 - (clinicalIndication?.length || 0)} left
              </Typography>
            </Box>
            <TextField
              multiline
              rows={2}
              placeholder="Enter clinical indication or reason for examination..."
              fullWidth
              value={clinicalIndication}
              onChange={(e) => setClinicalIndication(e.target.value.slice(0, 500))}
              InputProps={{
                sx: {
                  borderRadius: '6px',
                  fontFamily: '"Satoshi", sans-serif',
                  bgcolor: inputBg,
                  border: cardBorder,
                  color: textColorPrimary,
                  fontSize: '0.75rem',
                  p: 0.75,
                  '& textarea': { color: textColorPrimary, fontSize: '0.75rem' },
                  '& fieldset': { border: 'none' },
                  '&:hover': { borderColor: hoverBorderColor },
                  '&.Mui-focused': { borderColor: '#00665C' }
                }
              }}
            />

            {/* Compact Attachment Row */}
            <Box sx={{ mt: 0.8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Typography variant="caption" sx={{ ...satoshiBold, color: textColorSecondary, fontSize: '0.70rem' }}>
                Clinical Notes / Referral (Optional):
              </Typography>
              <ButtonBase
                component="label"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.6,
                  py: 0.3,
                  px: 0.8,
                  borderRadius: '6px',
                  border: `1px dashed ${isDark ? 'rgba(255,255,255,0.2)' : cardBorderColor}`,
                  bgcolor: inputBg,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    borderColor: '#00665C',
                    bgcolor: hoverBgSubtle
                  }
                }}
              >
                <input type="file" hidden onChange={handleFileChange} />
                <AttachFileIcon sx={{ color: '#64748B', fontSize: 14 }} />
                <Typography sx={{ ...satoshiBold, fontSize: '0.68rem', color: textColorPrimary, maxWidth: 180 }} noWrap>
                  {attachedFile ? attachedFile.name : 'Upload file (PDF, JPG, PNG)'}
                </Typography>
              </ButtonBase>
            </Box>
          </Paper>

          {/* Ordering Information & Assignment Card */}
          <Paper
            elevation={0}
            sx={{
              p: 1.1,
              mb: 1.1,
              borderRadius: '8px',
              border: cardBorder,
              bgcolor: isDark ? '#181818' : '#FBFDFD',
              textAlign: 'left'
            }}
          >
            <Typography variant="subtitle2" sx={{ ...satoshiBold, color: textColorPrimary, mb: 0.6, fontSize: '0.78rem' }}>
              Ordering & Assignment (Optional)
            </Typography>

            <Grid container spacing={1} sx={{ mb: 0.8 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ ...satoshiBold, color: textColorSecondary, display: 'block', mb: 0.25, fontSize: '0.68rem' }}>
                  Referring Physician
                </Typography>
                <Autocomplete
                  freeSolo
                  options={referrers}
                  getOptionLabel={(r) => {
                    if (typeof r === 'string') return r;
                    return `${r.degree || ''} ${r.firstName} ${r.lastName}`.trim();
                  }}
                  value={referrerName}
                  onInputChange={(_, value) => {
                    const match = referrers.find(
                      ref => `${ref.degree || ''} ${ref.firstName} ${ref.lastName}`.trim().toLowerCase() === value.trim().toLowerCase()
                    );
                    setReferrer(match ? match._id : null);
                    setReferrerName(value);
                  }}
                  onChange={(_, r) => {
                    if (typeof r === 'string') {
                      setReferrer(null);
                      setReferrerName(r);
                    } else if (r) {
                      setReferrer(r._id);
                      setReferrerName(`${r.degree || ''} ${r.firstName} ${r.lastName}`.trim());
                    } else {
                      setReferrer(null);
                      setReferrerName('');
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Physician name..."
                      fullWidth
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        sx: {
                          borderRadius: '6px',
                          height: 28,
                          fontFamily: '"Satoshi", sans-serif',
                          bgcolor: inputBg,
                          border: cardBorder,
                          color: textColorPrimary,
                          fontSize: '0.75rem',
                          '& input': { color: textColorPrimary, py: '2px', fontSize: '0.75rem' },
                          '& fieldset': { border: 'none' },
                          '&:hover': { borderColor: hoverBorderColor },
                          '&.Mui-focused': { borderColor: '#00665C' }
                        }
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ ...satoshiBold, color: textColorSecondary, display: 'block', mb: 0.25, fontSize: '0.68rem' }}>
                  Department
                </Typography>
                <Autocomplete
                  freeSolo
                  options={departments}
                  getOptionLabel={(d) => {
                    if (typeof d === 'string') return d;
                    return d.nameEn ? `${d.name} (${d.nameEn})` : d.name;
                  }}
                  value={departmentName}
                  onInputChange={(_, value) => {
                    const match = departments.find(
                      dep => dep.name.toLowerCase() === value.trim().toLowerCase() ||
                             (dep.nameEn && dep.nameEn.toLowerCase() === value.trim().toLowerCase())
                    );
                    setDepartment(match ? match._id : null);
                    setDepartmentName(value);
                  }}
                  onChange={(_, d) => {
                    if (typeof d === 'string') {
                      setDepartment(null);
                      setDepartmentName(d);
                    } else if (d) {
                      setDepartment(d._id);
                      setDepartmentName(d.name);
                    } else {
                      setDepartment(null);
                      setDepartmentName('');
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select department..."
                      fullWidth
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        sx: {
                          borderRadius: '6px',
                          height: 28,
                          fontFamily: '"Satoshi", sans-serif',
                          bgcolor: inputBg,
                          border: cardBorder,
                          color: textColorPrimary,
                          fontSize: '0.75rem',
                          '& input': { color: textColorPrimary, py: '2px', fontSize: '0.75rem' },
                          '& fieldset': { border: 'none' },
                          '&:hover': { borderColor: hoverBorderColor },
                          '&.Mui-focused': { borderColor: '#00665C' }
                        }
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>

            {/* Assign Radiologist */}
            <Box>
              <Typography variant="caption" sx={{ ...satoshiBold, color: textColorSecondary, display: 'block', mb: 0.25, fontSize: '0.68rem' }}>
                Assign Radiologist
              </Typography>
              <Autocomplete
                options={radiologists}
                getOptionLabel={(r) => {
                  if (!r || typeof r === 'string') return r || '';
                  return `${r.firstName} ${r.lastName}${r.specialty ? ` — ${r.specialty}` : ''}`;
                }}
                value={selectedRadiologist}
                onChange={(_, r) => setSelectedRadiologist(r)}
                isOptionEqualToValue={(option, value) => option?._id === value?._id}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option._id} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important', gap: 0.2, py: 0.4 }}>
                    <Typography sx={{ ...satoshiBold, fontSize: '0.75rem', color: textColorPrimary }}>
                      {option.firstName} {option.lastName}
                    </Typography>
                    <Typography sx={{ ...satoshiRegular, fontSize: '0.66rem', color: textColorSecondary }}>
                      {option.specialty || 'General Radiology'} {option.isAvailable === false ? '• Unavailable' : ''}
                    </Typography>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search radiologist by name..."
                    fullWidth
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: '#94A3B8', fontSize: 16 }} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      sx: {
                        borderRadius: '6px',
                        height: 28,
                        fontFamily: '"Satoshi", sans-serif',
                        bgcolor: inputBg,
                        border: cardBorder,
                        color: textColorPrimary,
                        fontSize: '0.75rem',
                        '& input': { color: textColorPrimary, py: '2px', fontSize: '0.75rem' },
                        '& fieldset': { border: 'none' },
                        '&:hover': { borderColor: '#CBD5E1' },
                        '&.Mui-focused': { borderColor: '#00665C' }
                      }
                    }}
                  />
                )}
                noOptionsText="No radiologists available"
              />
              {selectedRadiologist && (
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.5,
                  p: 0.4, px: 0.8, bgcolor: isDark ? 'rgba(13,148,136,0.15)' : '#F0FDFA', borderRadius: '6px',
                  border: '1px solid #99F6E4'
                }}>
                  <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: '#0D9488', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ ...satoshiBold, color: '#fff', fontSize: '0.65rem' }}>
                      {selectedRadiologist.firstName?.[0]}{selectedRadiologist.lastName?.[0]}
                    </Typography>
                  </Box>
                  <Typography sx={{ ...satoshiBold, fontSize: '0.72rem', color: textColorPrimary }}>
                    {selectedRadiologist.firstName} {selectedRadiologist.lastName}
                  </Typography>
                  <Typography sx={{ ...satoshiRegular, fontSize: '0.65rem', color: textColorSecondary }}>
                    ({selectedRadiologist.specialty || 'General'})
                  </Typography>
                  <IconButton size="small" onClick={() => setSelectedRadiologist(null)} sx={{ ml: 'auto', color: textColorPrimary, p: 0.2 }}>
                    <CloseIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Paper>

          {/* Scheduling Card */}
          <Paper
            elevation={0}
            sx={{
              p: 1.1,
              borderRadius: '8px',
              border: cardBorder,
              bgcolor: isDark ? '#181818' : '#FBFDFD',
              textAlign: 'left'
            }}
          >
            <Typography variant="subtitle2" sx={{ ...satoshiBold, color: textColorPrimary, mb: 0.5, fontSize: '0.78rem' }}>
              Scheduling & Equipment (Optional)
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={availableRooms.length > 0 ? 4 : 6}>
                <Typography variant="caption" sx={{ ...satoshiBold, color: textColorSecondary, display: 'block', mb: 0.25, fontSize: '0.68rem' }}>
                  Exam Date
                </Typography>
                <TextField
                  type="date"
                  fullWidth
                  size="small"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  InputProps={{
                    sx: {
                      borderRadius: '6px',
                      height: 28,
                      fontFamily: '"Satoshi", sans-serif',
                      bgcolor: inputBg,
                      border: cardBorder,
                      color: textColorPrimary,
                      fontSize: '0.75rem',
                      '& input': { color: textColorPrimary, colorScheme: isDark ? 'dark' : 'light', py: '2px', fontSize: '0.75rem' },
                      '& fieldset': { border: 'none' },
                      '&:hover': { borderColor: hoverBorderColor },
                      '&.Mui-focused': { borderColor: '#00665C' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={availableRooms.length > 0 ? 4 : 6}>
                <Typography variant="caption" sx={{ ...satoshiBold, color: textColorSecondary, display: 'block', mb: 0.25, fontSize: '0.68rem' }}>
                  Exam Time
                </Typography>
                <TextField
                  type="time"
                  fullWidth
                  size="small"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  InputProps={{
                    sx: {
                      borderRadius: '6px',
                      height: 28,
                      fontFamily: '"Satoshi", sans-serif',
                      bgcolor: inputBg,
                      border: cardBorder,
                      color: textColorPrimary,
                      fontSize: '0.75rem',
                      '& input': { color: textColorPrimary, colorScheme: isDark ? 'dark' : 'light', py: '2px', fontSize: '0.75rem' },
                      '& fieldset': { border: 'none' },
                      '&:hover': { borderColor: hoverBorderColor },
                      '&.Mui-focused': { borderColor: '#00665C' }
                    }
                  }}
                />
              </Grid>
              {availableRooms.length > 0 && (
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" sx={{ ...satoshiBold, color: textColorSecondary, display: 'block', mb: 0.25, fontSize: '0.68rem' }}>
                    Room / Equipment
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={selectedRoom?._id || ''}
                    onChange={(e) => setSelectedRoom(availableRooms.find(r => r._id === e.target.value) || null)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        height: 28,
                        bgcolor: inputBg,
                        border: cardBorder,
                        fontSize: '0.75rem',
                        '& .MuiSelect-select': { py: '2px' }
                      }
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: '0.75rem' }}>Unassigned</MenuItem>
                    {availableRooms.map((room) => (
                      <MenuItem key={room._id} value={room._id} sx={{ fontSize: '0.75rem' }}>{room.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  const headerActions = (
    <Box sx={{ display: 'flex', gap: 0.75 }}>
      <Button
        variant="outlined"
        disabled={loading}
        onClick={onClose}
        startIcon={<CloseIcon sx={{ fontSize: 14 }} />}
        sx={{
          ...satoshiBold,
          textTransform: 'none',
          color: textColorPrimary,
          borderColor: cardBorderColor,
          borderRadius: '6px',
          px: 1.5,
          py: 0,
          height: 28,
          fontSize: '0.74rem',
          '&:hover': {
            borderColor: '#94A3B8',
            bgcolor: hoverBgSubtle
          }
        }}
      >
        Cancel
      </Button>
      <Button
        variant="contained"
        disabled={loading || !patient}
        onClick={handleSubmit}
        startIcon={
          loading ? (
            <CircularProgress size={14} color="inherit" />
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="12" y1="18" x2="12" y2="12" />
              <line x1="9" y1="15" x2="15" y2="15" />
            </svg>
          )
        }
        sx={{
          ...satoshiBold,
          textTransform: 'none',
          color: '#FFFFFF',
          bgcolor: '#00665C',
          borderRadius: '6px',
          px: 1.75,
          py: 0,
          height: 28,
          fontSize: '0.74rem',
          boxShadow: 'none',
          '&:hover': {
            bgcolor: '#004D47',
            boxShadow: 'none'
          },
          '&.Mui-disabled': {
            bgcolor: '#E2E8F0',
            color: '#94A3B8'
          }
        }}
      >
        {loading ? (editOrder ? 'Saving...' : 'Creating...') : (editOrder ? 'Save Changes' : 'Create Order')}
      </Button>
    </Box>
  );

  const titleHeader = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, textAlign: 'left' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 28,
          height: 28,
          borderRadius: '6px',
          bgcolor: isDark ? 'rgba(13, 148, 136, 0.15)' : '#E6F4F2',
          color: '#00665C',
        }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="12" y1="18" x2="12" y2="12" />
          <line x1="9" y1="15" x2="15" y2="15" />
        </svg>
      </Box>
      <Box>
        <Typography variant="h6" sx={{ ...satoshiBold, color: textColorPrimary, fontSize: '0.90rem', lineHeight: 1.1 }}>
          {editOrder ? 'Edit Order' : 'Create Order'}
        </Typography>
        <Typography variant="body2" sx={{ ...satoshiMedium, color: textColorSecondary, fontSize: '0.68rem', lineHeight: 1 }}>
          {editOrder ? 'Modify order details and priority' : 'Select patient and enter examination details'}
        </Typography>
      </Box>
    </Box>
  );

  const renderSuccessModalContent = () => (
    <Box sx={{ py: 0.5, px: 1.5, textAlign: 'center', position: 'relative' }}>
      {/* Close button inside modal dialog header */}
      <IconButton
        onClick={handleResetForm}
        size="small"
        sx={{ position: 'absolute', right: -4, top: -4, color: textColorSecondary }}
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>

      {/* Success checkmark with floating dots */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5, mb: 1.5, position: 'relative' }}>
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: '50%',
            bgcolor: '#10B981',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#FFFFFF',
            boxShadow: '0 6px 16px rgba(16, 185, 129, 0.25)',
          }}
        >
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </Box>
        {/* Floating circles decoration */}
        <Box sx={{ position: 'absolute', width: 7, height: 7, borderRadius: '50%', bgcolor: '#34D399', top: 6, left: '38%' }} />
        <Box sx={{ position: 'absolute', width: 5, height: 5, borderRadius: '50%', bgcolor: '#6EE7B7', top: 28, left: '33%' }} />
        <Box sx={{ position: 'absolute', width: 8, height: 8, borderRadius: '50%', bgcolor: '#A7F3D0', bottom: 6, right: '35%' }} />
        <Box sx={{ position: 'absolute', width: 5, height: 5, borderRadius: '50%', bgcolor: '#34D399', top: 16, right: '32%' }} />
      </Box>

      {/* Title */}
      <Typography variant="h5" sx={{ ...satoshiBold, color: textColorPrimary, fontSize: '1.2rem', mb: 0.5, letterSpacing: '-0.02em' }}>
        {editOrder ? 'Order Updated Successfully!' : 'Order Created Successfully!'}
      </Typography>

      {/* Subtitle */}
      <Typography variant="body2" sx={{ ...satoshiMedium, color: textColorSecondary, fontSize: '0.8rem', mb: 2, px: 1, lineHeight: 1.4 }}>
        {editOrder ? 'The imaging order has been successfully updated.' : 'The imaging order has been successfully added to the worklist.'}
      </Typography>

      {/* Details Container */}
      <Box
        sx={{
          border: cardBorder,
          borderRadius: '10px',
          p: 1.5,
          mb: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          bgcolor: isDark ? '#141414' : '#F8FAFC',
          textAlign: 'left'
        }}
      >
        {/* Patient row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            <Typography sx={{ ...satoshiBold, color: textColorSecondary, fontSize: '0.8rem' }}>Patient</Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={{ ...satoshiBold, color: textColorPrimary, fontSize: '0.825rem' }}>
              {createdOrderDetails?.patientName}
            </Typography>
            <Typography sx={{ ...satoshiMedium, color: textColorSecondary, fontSize: '0.72rem' }}>
              {createdOrderDetails?.patientMrn}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: cardBorderColor }} />

        {/* Examination row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
            <Typography sx={{ ...satoshiBold, color: textColorSecondary, fontSize: '0.8rem' }}>Examination</Typography>
          </Box>
          <Typography sx={{ ...satoshiBold, color: textColorPrimary, fontSize: '0.825rem' }}>
            {createdOrderDetails?.examination}
          </Typography>
        </Box>

        <Divider sx={{ borderColor: cardBorderColor }} />

        {/* View row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            <Typography sx={{ ...satoshiBold, color: textColorSecondary, fontSize: '0.8rem' }}>View</Typography>
          </Box>
          <Typography sx={{ ...satoshiBold, color: textColorPrimary, fontSize: '0.825rem' }}>
            {createdOrderDetails?.view}
          </Typography>
        </Box>

        <Divider sx={{ borderColor: cardBorderColor }} />

        {/* Priority row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
              <line x1="4" y1="22" x2="4" y2="15" />
            </svg>
            <Typography sx={{ ...satoshiBold, color: textColorSecondary, fontSize: '0.8rem' }}>Priority</Typography>
          </Box>
          <Typography
            sx={{
              ...satoshiBold,
              color: createdOrderDetails?.priority === 'STAT' ? '#EF4444' : '#0D9488',
              fontSize: '0.825rem'
            }}
          >
            {createdOrderDetails?.priority === 'STAT' ? 'STAT' : 'Routine'}
          </Typography>
        </Box>

        <Divider sx={{ borderColor: cardBorderColor }} />

        {/* Status row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <Typography sx={{ ...satoshiBold, color: textColorSecondary, fontSize: '0.8rem' }}>Status</Typography>
          </Box>
          <Box
            sx={{
              px: 1.5,
              py: 0.25,
              borderRadius: '20px',
              bgcolor: isDark ? 'rgba(37, 99, 235, 0.15)' : '#EFF6FF',
              border: '1.5px solid #BFDBFE',
              color: textColorPrimary,
              fontSize: '0.72rem',
              fontWeight: 700,
              fontFamily: '"Satoshi", sans-serif'
            }}
          >
            New Order
          </Box>
        </Box>
      </Box>

      {/* Buttons */}
      <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, mb: 0.5 }}>
        <Button
          variant="outlined"
          onClick={editOrder ? () => setShowSuccessModal(false) : handleResetFormKeepPatient}
          fullWidth
          startIcon={
            editOrder ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            )
          }
          sx={{
            ...satoshiBold,
            textTransform: 'none',
            color: '#00665C',
            borderColor: '#00665C',
            border: '1.5px solid',
            borderRadius: '8px',
            height: '34px',
            fontSize: '0.82rem',
            bgcolor: cardBg,
            '&:hover': {
              borderColor: '#004D47',
              bgcolor: isDark ? 'rgba(13, 148, 136, 0.1)' : '#F0FDFA'
            }
          }}
        >
          {editOrder ? 'Edit Order' : 'Create Another'}
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            if (onSuccess) onSuccess(createdOrderDetails, 'worklist');
            if (onClose) onClose();
          }}
          fullWidth
          startIcon={
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <line x1="9" y1="9" x2="15" y2="9" />
              <line x1="9" y1="13" x2="15" y2="13" />
              <line x1="9" y1="17" x2="15" y2="17" />
            </svg>
          }
          sx={{
            ...satoshiBold,
            textTransform: 'none',
            color: '#FFFFFF',
            bgcolor: '#005B4F',
            borderRadius: '8px',
            height: '34px',
            fontSize: '0.82rem',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: '#004D47',
              boxShadow: 'none'
            }
          }}
        >
          Go to Worklist
        </Button>
      </Box>
    </Box>
  );

  // Dialog Mode
  if (open !== undefined) {
    return (
      <>
        <Dialog
          open={open && !showSuccessModal}
          onClose={onClose}
          maxWidth={false}
          PaperProps={{
            sx: {
              width: { xs: '95vw', sm: '92vw', md: '940px' },
              maxWidth: '960px !important',
              maxHeight: '88vh',
              borderRadius: '14px',
              p: 1,
              bgcolor: cardBg,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.25 }}>
            {titleHeader}
            <IconButton onClick={onClose} size="small" sx={{ color: textColorSecondary }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
          <DialogContent dividers sx={{ borderTop: cardBorder, borderBottom: cardBorder, px: 2, py: 1.5, overflowY: 'auto' }}>
            {formContent}
          </DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, px: 2, py: 1.25 }}>
            {headerActions}
          </Box>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={() => setSnackbar(s => ({ ...s, open: false }))}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert severity={snackbar.severity} variant="filled" sx={{ ...satoshiBold }}>
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Dialog>

        {/* Success Modal Overlay (both dialog / page mode) */}
        <Dialog
          open={showSuccessModal}
          onClose={handleResetForm}
          maxWidth={false}
          PaperProps={{
            sx: {
              width: { xs: '95vw', sm: '460px' },
              maxWidth: '480px !important',
              maxHeight: '85vh',
              borderRadius: '16px',
              p: 2,
              bgcolor: cardBg,
              overflow: 'hidden'
            }
          }}
        >
          {renderSuccessModalContent()}
        </Dialog>
      </>
    );
  }

  // Standalone Page Mode
  return (
    <Box sx={{ width: '100%', maxWidth: '1440px', mx: 'auto', p: { xs: 0.5, md: 0.75 } }}>
      {/* Title Card Header */}
      <Paper
        elevation={0}
        sx={{
          py: 0.75,
          px: 1.5,
          mb: 1,
          borderRadius: '8px',
          border: cardBorder,
          bgcolor: cardBg,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1
        }}
      >
        {titleHeader}
        {headerActions}
      </Paper>

      {/* Main Form Content */}
      {formContent}

      {/* Success Modal Overlay */}
      <Dialog
        open={showSuccessModal}
        onClose={handleResetForm}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '460px' },
            maxWidth: '480px !important',
            maxHeight: '85vh',
            borderRadius: '16px',
            p: 2,
            bgcolor: cardBg,
            overflow: 'hidden'
          }
        }}
      >
        {renderSuccessModalContent()}
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ ...satoshiBold }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
